/**
 * cstUtils.js — America/Chicago timezone helpers shared across the app.
 */

export const TZ = 'America/Chicago';

/** Return a Date that represents local midnight in America/Chicago for the given Date. */
export function tzDate(date) {
  const iso = date.toLocaleDateString('en-CA', { timeZone: TZ });
  return new Date(`${iso}T00:00:00`);
}

/** Zero-pad a number to 2 digits. */
export function padN(n) { return String(n).padStart(2, '0'); }

/** Format a Date as a value usable in <input type="datetime-local">. */
export function toLocalInput(d) {
  return `${d.getFullYear()}-${padN(d.getMonth() + 1)}-${padN(d.getDate())}T${padN(d.getHours())}:${padN(d.getMinutes())}`;
}

/**
 * Convert any Date into an RFC 3339 string using America/Chicago wall-clock
 * time + correct CST/CDT UTC offset — exactly what Square API requires.
 *
 * e.g. a Date at 2026-04-28T19:00:00Z →  "2026-04-28T14:00:00-05:00"  (CDT)
 *      a Date at 2026-12-01T18:00:00Z →  "2026-12-01T12:00:00-06:00"  (CST)
 *
 * Strategy:
 *   1. Ask Intl what the Chicago wall-clock components are for this UTC moment.
 *   2. Ask Intl what the Chicago UTC offset string is for this UTC moment.
 *   3. Assemble: "YYYY-MM-DDTHH:MM:SS±HH:MM"  — no arithmetic, no UTC conversion.
 */
export function toCST_ISO(date) {
  // Step 1: Chicago wall-clock components
  const wallFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const p = wallFmt.formatToParts(date);
  const get = t => p.find(x => x.type === t)?.value ?? '00';
  let hh = parseInt(get('hour'), 10);
  if (hh === 24) hh = 0; // midnight edge-case in some Intl implementations

  const datePart = `${get('year')}-${get('month')}-${get('day')}`;
  const timePart = `${padN(hh)}:${get('minute')}:${get('second')}`;

  // Step 2: Chicago UTC offset for this exact moment ("GMT-5" CDT, "GMT-6" CST)
  const offRaw = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, timeZoneName: 'shortOffset',
  }).formatToParts(date).find(x => x.type === 'timeZoneName')?.value || 'GMT-6';

  // offRaw examples: "GMT-5", "GMT-6", "GMT+5:30"
  const om = offRaw.match(/GMT([+-])(\d+)(?::(\d+))?/);
  const offSign  = om ? om[1] : '-';
  const offHours = om ? padN(parseInt(om[2], 10)) : '06';
  const offMins2 = om && om[3] ? om[3] : '00';

  return `${datePart}T${timePart}${offSign}${offHours}:${offMins2}`;
}

/**
 * Format an ISO timestamp as a human-readable CST/CDT date+time string.
 * e.g. "Apr 28, 2:34 PM"
 */
export function fmtTimeCst(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
